/**
 * API 客户端 - 基础类
 */
import type { ApiResponse, ApiError } from './types';
import { normalizeApiError, normalizeApiResponse } from './types';
import { API_BASE_URL } from './base-url';
import { getLocalGcpUploadProxyEndpoint } from './local-gcp-upload-routing';
export { API_BASE_URL } from './base-url';
import { translateClient } from '@/lib/i18n/client';
import {
  assertFreshClientBeforeCriticalAction,
  ClientVersionStaleError,
  getClientBuildHeaders,
  isCriticalClientMutation,
} from '@/lib/version/client-version';
import { 
  getAuthToken as getAuthTokenFromStore, 
  ensureValidToken as ensureValidTokenFromStore,
  getAuthSessionVersion,
  useAuthStore 
} from '@/stores/authStore';
import { handleAuthExpiredRedirect } from '@/lib/auth/session';
import { recordApiRequestDiagnostic } from '@/lib/client-errors';

// 调试开关
const DEBUG_API = false;
const apiLog = (...args: unknown[]) => { if (DEBUG_API) console.log(...args); };
const apiWarn = (...args: unknown[]) => { if (DEBUG_API) console.warn(...args); };

export const CLIENT_SURFACE_HEADER = 'X-Client-Surface';
export const WEB_CLIENT_SURFACE = 'web';

interface RequestAuthContext {
  version: number;
  hadAuthenticatedContext: boolean;
}

function captureRequestAuthContext(): RequestAuthContext {
  const state = useAuthStore.getState();
  return {
    version: getAuthSessionVersion(),
    hadAuthenticatedContext: state.isAuthenticated || Boolean(state.accessToken),
  };
}

function sessionChangedFailure(): ApiResponse<never> {
  return apiFailure({
    code: 'AUTH_SESSION_CHANGED',
    message: translateClient('errors', 'codes.unauthorized.message'),
    retryable: false,
  });
}

function withoutClientSurfaceHeader(headers: RequestInit['headers']): Record<string, string> {
  return Object.fromEntries(
    Object.entries((headers as Record<string, string> | undefined) ?? {})
      .filter(([key]) => key.toLowerCase() !== CLIENT_SURFACE_HEADER.toLowerCase()),
  );
}

function sanitizeApiErrorMessage(message: string): string {
  const normalized = message.trim();
  if (!normalized) return normalized;

  const lowered = normalized.toLowerCase();
  if (
    lowered.includes('violates check constraint')
    || lowered.includes('new row for relation')
    || lowered.includes('failing row contains')
    || lowered.includes('tasks_status_check')
  ) {
    return translateClient('errors', 'codes.serviceUnavailable.message');
  }

  return normalized;
}

function readApiErrorMessage(errorData: Record<string, unknown>, fallbackMessage: string): string {
  const detail = errorData.detail;
  if (typeof detail === 'object' && detail !== null && 'message' in detail) {
    const detailMessage = (detail as { message?: unknown }).message;
    if (typeof detailMessage === 'string' && detailMessage.trim()) {
      return sanitizeApiErrorMessage(detailMessage);
    }
  }
  if (typeof detail === 'string' && detail.trim()) {
    return sanitizeApiErrorMessage(detail);
  }
  if (typeof errorData.message === 'string' && errorData.message.trim()) {
    return sanitizeApiErrorMessage(errorData.message);
  }
  return sanitizeApiErrorMessage(fallbackMessage);
}

function requestIdFromResponse(response?: Response): string | undefined {
  return response?.headers.get('X-Request-ID') || undefined;
}

function apiFailure(error: ApiError, response?: Response): ApiResponse<never> {
  return {
    success: false,
    error: {
      ...error,
      httpStatus: response?.status ?? error.httpStatus,
      requestId: error.requestId ?? requestIdFromResponse(response),
    },
  };
}

// 防止并发刷新 token
let isRefreshingToken = false;
let refreshTokenPromise: Promise<boolean> | null = null;

// ============================================
// 认证辅助函数
// ============================================

/** 获取认证 Token - 直接从 auth-store 内存读取 */
export function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  
  // 直接从 zustand store 内存获取（最新值）
  return getAuthTokenFromStore();
}

/** 
 * ★ 确保 token 有效 - 在关键 API 调用前使用
 * 如果 token 即将过期会主动刷新
 */
export async function ensureValidToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  return ensureValidTokenFromStore();
}

export async function ensureValidTokenForce(forceRefresh = false): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  return ensureValidTokenFromStore(forceRefresh);
}

export async function forceRefreshToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  return ensureValidTokenFromStore(true);
}

/** 
 * 尝试刷新 Token
 * 使用 auth-store 实例，避免创建多个 GoTrueClient
 */
async function tryRefreshToken(): Promise<boolean> {
  // 防止并发刷新
  if (isRefreshingToken && refreshTokenPromise) {
    return refreshTokenPromise;
  }
  
  isRefreshingToken = true;
  
  refreshTokenPromise = (async () => {
    try {
      apiLog('[API] Attempting to refresh token via auth-store...');
      const refreshedToken = await ensureValidTokenFromStore(true);
      
      // 检查刷新后的 token（直接从内存读取）
      const newToken = refreshedToken ?? useAuthStore.getState().accessToken;
      if (newToken) {
        apiLog('[API] Token refreshed successfully via auth-store');
        return true;
      }
      
      apiWarn('[API] Token refresh failed: no valid session');
      return false;
    } catch (e) {
      console.error('[API] Token refresh error:', e);
      return false;
    } finally {
      isRefreshingToken = false;
      refreshTokenPromise = null;
    }
  })();
  
  return refreshTokenPromise;
}

/** 处理登录过期：清除本地状态并跳转到登录页 */
export function handleAuthExpired() {
  if (typeof window === 'undefined') return;
  void handleAuthExpiredRedirect().catch(() => {});
}

// ============================================
// API 客户端基类
// ============================================

export class ApiClient {
  protected baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  protected async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryOnAuth: boolean = true,  // 是否在 401 时尝试刷新 token 后重试
    authContext: RequestAuthContext = captureRequestAuthContext(),
  ): Promise<ApiResponse<T>> {
    const endpointForDiagnostic = endpoint;
    try {
      const method = (options.method || 'GET').toUpperCase();
      const url = getLocalGcpUploadProxyEndpoint(endpoint, method) ?? `${this.baseUrl}${endpoint}`;

      if (isCriticalClientMutation(method)) {
        await assertFreshClientBeforeCriticalAction();
      }
      if (getAuthSessionVersion() !== authContext.version) return sessionChangedFailure();
      
      // 构建请求头，自动注入 Authorization + Request ID
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Request-ID': crypto.randomUUID().replace(/-/g, '').slice(0, 12),
        ...getClientBuildHeaders(),
        ...withoutClientSurfaceHeader(options.headers),
        // Keep the trusted surface after caller headers so feature modules
        // cannot accidentally impersonate the App surface.
        [CLIENT_SURFACE_HEADER]: WEB_CLIENT_SURFACE,
      };
      
      // ★★★ 关键修复：请求前先确保 token 有效 ★★★
      // 如果 token 即将过期（<5分钟），会主动刷新
      const token = await ensureValidToken();
      if (getAuthSessionVersion() !== authContext.version) return sessionChangedFailure();
      // 保留续期前的认证事实：明确拒绝可在 ensureValidToken 内清空 store。
      // 无本地信号的 HttpOnly cookie 恢复成功也属于当前请求的认证上下文。
      const hadAuthenticatedContext = authContext.hadAuthenticatedContext || Boolean(token);
      authContext = { ...authContext, hadAuthenticatedContext };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      let response = await fetch(url, {
        ...options,
        headers,
      });
      if (response.status === 202 || response.status === 504) {
        const {observeAcceptedOperationResponse} = await import('./accepted-operation-response');
        response = await observeAcceptedOperationResponse(response, {envelope: true, signal: options.signal});
      }
      recordApiRequestDiagnostic({
        requestId: requestIdFromResponse(response) || headers['X-Request-ID'],
        endpoint: endpointForDiagnostic,
        status: response.status,
      });
      // 旧请求不能在退出/重新登录后续期、重放或清掉新会话。
      if (getAuthSessionVersion() !== authContext.version) return sessionChangedFailure();

      // 处理认证失败 - 尝试刷新 token 后重试
      if (response.status === 401) {
        if (retryOnAuth && hadAuthenticatedContext) {
          apiWarn('[API] Unauthorized request, attempting token refresh...');
          const refreshed = await tryRefreshToken();
          if (getAuthSessionVersion() !== authContext.version) return sessionChangedFailure();
          
          if (refreshed) {
            apiLog('[API] Retrying request with new token...');
            return this.request<T>(endpoint, options, false, authContext);
          }
        }
        
        // ★ 仅在服务端明确拒绝时跳转登录；网络瞬断保留登录态
        const stillAuthenticated = hadAuthenticatedContext && useAuthStore.getState().isAuthenticated;
        if (hadAuthenticatedContext && !stillAuthenticated) {
          apiWarn('[API] Token definitively rejected, redirecting to login');
          handleAuthExpired();
        }
        return {
          success: false,
          error: {
            code: stillAuthenticated ? 'SERVICE_UNAVAILABLE' : 'UNAUTHORIZED',
            message: stillAuthenticated
              ? translateClient('errors', 'codes.serviceUnavailable.message')
              : translateClient('errors', 'codes.unauthorized.message'),
            action: stillAuthenticated ? 'retry' : 'login',
            retryable: stillAuthenticated,
            requestId: requestIdFromResponse(response),
          },
        };
      }

      // Return the credit failure to the initiating action; reads never open checkout.
      if (response.status === 402) {
        const errorData = await response.json().catch(() => ({}));
        return apiFailure(normalizeApiError(errorData, translateClient('errors', 'common.fallbackMessage')), response);
      }

      // 处理权限不足
      if (response.status === 403) {
        if (retryOnAuth && hadAuthenticatedContext && !token) {
          apiWarn('[API] Forbidden without access token, attempting forced token refresh...');
          const refreshed = await tryRefreshToken();
          if (getAuthSessionVersion() !== authContext.version) return sessionChangedFailure();

          if (refreshed) {
            apiLog('[API] Retrying forbidden request with refreshed token...');
            return this.request<T>(endpoint, options, false, authContext);
          }

          if (!useAuthStore.getState().isAuthenticated) {
            handleAuthExpired();
          }
        }

        apiWarn('[API] Forbidden request');
        const errorData = await response.json().catch(() => ({})) as Record<string, unknown>;
        const normalized = normalizeApiError(errorData, readApiErrorMessage(errorData, response.statusText || 'Forbidden'));
        const failure = apiFailure({
          ...normalized,
          code: normalized.code === 'UNKNOWN_ERROR' ? 'FORBIDDEN' : normalized.code,
          action: normalized.action ?? 'check_access',
          retryable: false,
          details: normalized.details ?? errorData,
        }, response);
        return failure;
      }

      // 处理版本冲突
      if (response.status === 409) {
        const conflictData = await response.json().catch(() => ({})) as Record<string, unknown>;
        const normalized = normalizeApiError(
          conflictData,
          readApiErrorMessage(conflictData, response.statusText || 'Conflict'),
        );
        const conflictCode = conflictData.error === 'version_conflict'
          ? 'VERSION_CONFLICT'
          : normalized.code === 'UNKNOWN_ERROR'
            ? 'HTTP_409'
            : normalized.code;
        return apiFailure({
          ...normalized,
          code: conflictCode,
          retryable: normalized.retryable ?? conflictCode === 'VERSION_CONFLICT',
          details: conflictData,
        }, response);
      }

      // Only safe reads may replay a transient 503. A mutation/search POST may
      // already have incurred provider work even when its response was lost.
      if (response.status === 503) {
        const errorData = await response.json().catch(() => ({})) as Record<string, unknown>;
        const normalized = normalizeApiError(errorData, readApiErrorMessage(errorData, response.statusText));
        if (retryOnAuth && normalized.code === 'UNKNOWN_ERROR'
          && ['GET', 'HEAD'].includes((options.method ?? 'GET').toUpperCase())) {
          apiWarn('[API] 503 Service Unavailable, retrying after 1s...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          return this.request<T>(endpoint, options, false, authContext);
        }
        return apiFailure({
          ...normalized,
          code: normalized.code === 'UNKNOWN_ERROR' ? 'SERVICE_UNAVAILABLE' : normalized.code,
          retryable: normalized.retryable ?? false,
          details: normalized.details ?? errorData,
        }, response);
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as Record<string, unknown>;
        const normalized = normalizeApiError(errorData, readApiErrorMessage(errorData, response.statusText));
        return apiFailure({
          ...normalized,
          code: normalized.code === 'UNKNOWN_ERROR' ? `HTTP_${response.status}` : normalized.code,
          retryable: normalized.retryable ?? response.status >= 500,
          details: normalized.details ?? errorData,
        }, response);
      }

      const data = await response.json();
      const normalized = normalizeApiResponse<T>(data);
      if (normalized.success === false) {
        return apiFailure(normalized.error ?? normalizeApiError(data), response);
      }
      return {
        success: true,
        data: normalized.data as T,
      };
    } catch (error) {
      recordApiRequestDiagnostic({
        endpoint: endpointForDiagnostic,
        status: error instanceof ClientVersionStaleError ? 409 : undefined,
      });
      return {
        success: false,
        error: {
          code: error instanceof ClientVersionStaleError ? 'CLIENT_VERSION_STALE' : 'NETWORK_ERROR',
          message: error instanceof ClientVersionStaleError
            ? translateClient('common', 'refreshRequiredMessage')
            : translateClient('errors', 'codes.network.message'),
          action: error instanceof ClientVersionStaleError ? 'refresh' : 'retry',
          retryable: !(error instanceof ClientVersionStaleError),
          details: error instanceof Error ? { rawMessage: error.message } : undefined,
        },
      };
    }
  }
}
